import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api-fetch';
import type { CampaignLogEntry, ScheduledCampaign } from '../types/bulk-email-scheduling';

const REFRESH_MS = 10_000;

async function parseJson(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Server returned ${response.status}`);
  }
  return response.json();
}

export function useScheduledCampaigns() {
  const [campaigns, setCampaigns] = useState<ScheduledCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [logsCampaignId, setLogsCampaignId] = useState<string | null>(null);
  const [logs, setLogs] = useState<CampaignLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const prevStatuses = useRef<Map<string, string>>(new Map());

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/bulk-email/scheduled?status=all');
      const data = await parseJson(response);
      if (data.success) {
        const list = (data.campaigns || []) as ScheduledCampaign[];
        list.forEach((c) => {
          const prev = prevStatuses.current.get(c.id);
          if (prev && prev !== c.status) {
            if (c.status === 'processing')
              toast.info(`Campaign started: ${c.campaign_name || c.subject}`);
            if (c.status === 'completed')
              toast.success(`Campaign completed: ${c.campaign_name || c.subject}`);
            if (c.status === 'partially_completed')
              toast.warning(`Campaign partially completed: ${c.campaign_name || c.subject}`);
            if (c.status === 'failed')
              toast.error(`Campaign failed: ${c.campaign_name || c.subject}`);
            if (c.status === 'paused')
              toast.info(`Campaign paused: ${c.campaign_name || c.subject}`);
            if (prev === 'paused' && (c.status === 'pending' || c.status === 'queued')) {
              toast.success(`Campaign resumed: ${c.campaign_name || c.subject}`);
            }
          }
          prevStatuses.current.set(c.id, c.status);
        });
        setCampaigns(list);
        if (typeof data.message === 'string' && data.message.includes('migration')) {
          toast.warning('Run the scheduled email enhancement migration in Supabase.');
        }
      }
    } catch (error) {
      console.error('fetchCampaigns', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchCampaigns();
    const id = setInterval(() => void fetchCampaigns(), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchCampaigns]);

  const fetchLogs = useCallback(async (campaignId: string) => {
    setLogsLoading(true);
    setLogsCampaignId(campaignId);
    try {
      const response = await apiFetch(`/bulk-email/scheduled/${campaignId}/logs`);
      const data = await parseJson(response);
      if (data.success) setLogs((data.logs || []) as CampaignLogEntry[]);
    } catch {
      toast.error('Could not load campaign logs');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const cancelCampaign = useCallback(
    async (id: string) => {
      const response = await apiFetch(`/bulk-email/scheduled/${id}`, { method: 'DELETE' });
      const data = await parseJson(response);
      if (!data.success) throw new Error(String(data.error));
      toast.success('Campaign cancelled');
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  const pauseCampaign = useCallback(
    async (id: string) => {
      const response = await apiFetch(`/bulk-email/scheduled/${id}/pause`, { method: 'PATCH' });
      const data = await parseJson(response);
      if (!data.success) throw new Error(String(data.error));
      toast.success('Campaign paused');
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  const resumeCampaign = useCallback(
    async (id: string) => {
      const response = await apiFetch(`/bulk-email/scheduled/${id}/resume`, { method: 'PATCH' });
      const data = await parseJson(response);
      if (!data.success) throw new Error(String(data.error));
      toast.success('Campaign resumed');
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  const retryCampaign = useCallback(
    async (id: string) => {
      const response = await apiFetch(`/bulk-email/scheduled/${id}/retry`, { method: 'POST' });
      const data = await parseJson(response);
      if (!data.success) throw new Error(String(data.error));
      toast.success('Campaign queued for retry');
      await fetchCampaigns();
    },
    [fetchCampaigns]
  );

  const getNextPending = useCallback((): ScheduledCampaign | null => {
    const active = campaigns
      .filter((c) => ['pending', 'queued'].includes(c.status) && !c.paused_at)
      .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
    return active[0] || null;
  }, [campaigns]);

  return {
    campaigns,
    loading,
    fetchCampaigns,
    cancelCampaign,
    pauseCampaign,
    resumeCampaign,
    retryCampaign,
    logs,
    logsCampaignId,
    logsLoading,
    fetchLogs,
    setLogsCampaignId,
    getNextPending,
  };
}

export type UseScheduledCampaignsResult = ReturnType<typeof useScheduledCampaigns>;

export function formatCountdown(targetIso: string): string {
  const diff = new Date(targetIso).getTime() - Date.now();
  if (diff <= 0) return 'Due now';
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
}

export function formatInTimezone(isoUtc: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timeZone || 'UTC',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(isoUtc));
  } catch {
    return new Date(isoUtc).toLocaleString();
  }
}
